import { Divider, Switch } from 'antd';
import debounce from 'lodash/debounce';
import { useEffect, useMemo, useRef, useState, type UIEvent } from 'react';
import ChevronRight from 'assets/svg/ChevronRight';
import PpaSplitSearchInput, { PPA_SPLIT_SEARCH_WIDTH } from 'components/molecules/PPA/PpaSplitSearchInput/PpaSplitSearchInput';
import SimulatorFilterCategoryPanel from './SimulatorFilterCategoryPanel';
import SimulatorFilterPanelFooter from './SimulatorFilterPanelFooter';
import {
    DEFAULT_SIMULATOR_FILTER_OPTIONS,
    EMPTY_SIMULATOR_SHOW_ONLY_STATE,
    normalizeEditedActionSelectionKey,
    SIMULATOR_FILTER_CATEGORY_SEARCH_DEBOUNCE_MS,
    toSimulatorShowOnlyFilterItems,
    type SimulatorShowOnlyState,
} from './simulatorFilter.helpers';
import {
    PPA_SIM_FILTER_PANEL,
    PPA_SIM_FILTER_SEARCH_INPUT,
    PPA_SIM_FILTER_SEARCH_SUBMIT,
    PPA_SIM_FILTER_SHOW_EDITED_BTN,
    PPA_SIM_FILTER_SHOW_EDITED_TOGGLE,
    PPA_SIM_FILTER_SHOW_IMPACTED_TOGGLE,
    PPA_SIM_FILTER_SHOW_IMPACTED_BTN,
    ppaSimFilterCategoryBtnTestId,
} from 'constants/ppaTestIds';
import { useSimulatorFilterEditedActions } from './useSimulatorFilterEditedActions';
import { useSimulatorFilterAttributeNames } from './useSimulatorFilterAttributeNames';
import { useSimulatorFilterPanelApply } from './useSimulatorFilterPanelApply';
import {
    StyledFilterPanelsRoot,
    StyledPanel,
} from './SimulatorFilterPanel.styles';
import SearchEmptyResult from 'components/molecules/PricingSimulatorV3/SearchEmptyResult';
import type { SimulatorFilterPanelProps } from './SimulatorFilterPanel.types';

export type {
    SimulatorAppliedFilters,
    SimulatorCategoryApplyFilter,
    SimulatorCategoryAttributeCache,
    SimulatorFilterCategory,
    SimulatorFilterOption,
    SimulatorFilterScopeItem,
    SimulatorShowOnlyState,
} from './SimulatorFilterPanel.types';

function hasAppliedShowOnly(state: SimulatorShowOnlyState): boolean {
    return state.edited || state.impacted || state.editedFilters.length > 0;
}

// @code-guard: skip-next-function reason="filter panel component — JSX layout + draft state logic; split into sub-components tracked in backlog"
const SimulatorFilterPanel = ({
    filterCategories = [],
    editedActionTypes = [],
    scenarioId = null,
    viewId = null,
    viewDataSource = 'view',
    isOpen = false,
    appliedScope,
    appliedCategorySelections = {},
    appliedCategorySelectAll = {},
    appliedCategoryTotalCounts = {},
    appliedShowOnly = EMPTY_SIMULATOR_SHOW_ONLY_STATE,
    defaultShowOnly = EMPTY_SIMULATOR_SHOW_ONLY_STATE,
    showOnlyLabels = { edited: 'Edited', impacted: 'Impacted' },
    footerActions,
    isApplyLoading = false,
    onApply,
}: SimulatorFilterPanelProps) => {
    const [selectedCategoryKey, setSelectedCategoryKey] = useState<string | null>(null);
    const [isEditedActionsOpen, setIsEditedActionsOpen] = useState(false);
    const [categorySelections, setCategorySelections] = useState<Record<string, string[]>>({});
    const [categorySelectAll, setCategorySelectAll] = useState<Record<string, boolean>>({});
    const [draftShowOnly, setDraftShowOnly] = useState<SimulatorShowOnlyState>(defaultShowOnly);
    const [searchQuery, setSearchQuery] = useState('');
    const [categorySearchInput, setCategorySearchInput] = useState('');
    const [debouncedCategorySearch, setDebouncedCategorySearch] = useState('');

    const { actionTypes: fetchedEditedActionTypes, isLoading: isEditedActionsLoading } =
        useSimulatorFilterEditedActions({
            viewId,
            scenarioId,
            dataSource: viewDataSource,
            enabled: isOpen && isEditedActionsOpen,
            fallbackActionTypes: editedActionTypes,
        });

    const resolvedEditedActionTypes = isEditedActionsOpen
        ? fetchedEditedActionTypes
        : editedActionTypes;

    const appliedCategorySelectionsRef = useRef(appliedCategorySelections);
    appliedCategorySelectionsRef.current = appliedCategorySelections;
    const appliedCategorySelectAllRef = useRef(appliedCategorySelectAll);
    appliedCategorySelectAllRef.current = appliedCategorySelectAll;
    const appliedShowOnlyRef = useRef(appliedShowOnly);
    appliedShowOnlyRef.current = appliedShowOnly;
    const defaultShowOnlyRef = useRef(defaultShowOnly);
    defaultShowOnlyRef.current = defaultShowOnly;

    useEffect(() => {
        if (!isOpen) return;
        setCategorySelections(appliedCategorySelectionsRef.current);
        setCategorySelectAll(appliedCategorySelectAllRef.current);
        const applied = appliedShowOnlyRef.current;
        setDraftShowOnly(hasAppliedShowOnly(applied) ? applied : defaultShowOnlyRef.current);
        setSelectedCategoryKey(null);
        setIsEditedActionsOpen(false);
        setSearchQuery('');
        setCategorySearchInput('');
        setDebouncedCategorySearch('');
    }, [isOpen]);

    const debouncedSetCategorySearch = useMemo(
        () =>
            debounce((value: string) => {
                setDebouncedCategorySearch(value.trim());
            }, SIMULATOR_FILTER_CATEGORY_SEARCH_DEBOUNCE_MS),
        [],
    );

    useEffect(() => () => debouncedSetCategorySearch.cancel(), [debouncedSetCategorySearch]);

    useEffect(() => {
        const trimmed = categorySearchInput.trim();
        if (!trimmed) {
            debouncedSetCategorySearch.cancel();
            setDebouncedCategorySearch('');
            return;
        }
        debouncedSetCategorySearch(trimmed);
    }, [categorySearchInput, debouncedSetCategorySearch]);

    useEffect(() => {
        setCategorySearchInput('');
    }, [selectedCategoryKey]);

    const normalizedSearchQuery = searchQuery.trim().toLowerCase();
    const isFilterSearchActive = normalizedSearchQuery.length > 0;

    const visibleFilterCategories = useMemo(() => {
        if (!isFilterSearchActive) return filterCategories;
        return filterCategories.filter((category) =>
            category.label.toLowerCase().includes(normalizedSearchQuery),
        );
    }, [filterCategories, isFilterSearchActive, normalizedSearchQuery]);

    const hasNoFilterSearchResults = isFilterSearchActive && visibleFilterCategories.length === 0;

    const selectedCategory = useMemo(
        () => filterCategories.find((category) => category.key === selectedCategoryKey) ?? null,
        [filterCategories, selectedCategoryKey],
    );

    const canExpandEditedActions = editedActionTypes.length > 0 || viewId != null;

    const editedActionOptions = useMemo(
        () =>
            resolvedEditedActionTypes.map((action) => ({
                id: normalizeEditedActionSelectionKey(action.input_value),
                label: action.label,
            })),
        [resolvedEditedActionTypes],
    );

    const selectedEditedActionValues = useMemo(
        () =>
            draftShowOnly.editedFilters.map((filter) =>
                normalizeEditedActionSelectionKey(filter.input_value),
            ),
        [draftShowOnly.editedFilters],
    );

    const {
        options: fetchedCategoryOptions,
        totalCount: fetchedCategoryTotalCount,
        isLoading: isCategoryOptionsLoading,
        isFetchingNextPage: isCategoryOptionsFetchingMore,
        hasNextPage: hasMoreCategoryOptions,
        fetchNextPage: fetchMoreCategoryOptions,
    } = useSimulatorFilterAttributeNames({
        category: selectedCategory,
        appliedScope,
        showOnly: appliedShowOnly,
        searchKeyword: debouncedCategorySearch,
        enabled: isOpen && selectedCategory != null,
    });

    const handleCategoryOptionsScroll = (event: UIEvent<HTMLDivElement>) => {
        if (!hasMoreCategoryOptions || isCategoryOptionsFetchingMore) return;
        const target = event.currentTarget;
        if (target.scrollTop + target.clientHeight >= target.scrollHeight - 32) {
            void fetchMoreCategoryOptions();
        }
    };

    const selectedCategoryOptions = useMemo(() => {
        if (fetchedCategoryOptions.length > 0) {
            return fetchedCategoryOptions;
        }
        if (selectedCategory?.options?.length) {
            return selectedCategory.options;
        }
        return isCategoryOptionsLoading ? [] : DEFAULT_SIMULATOR_FILTER_OPTIONS;
    }, [fetchedCategoryOptions, isCategoryOptionsLoading, selectedCategory]);

    const { handleCategorySelectAllChange, handleCategoryOptionToggle, handleCategorySelectionChange, handleFooterAction, isApplyingFilters } =
        useSimulatorFilterPanelApply({
            isOpen,
            appliedCategoryTotalCounts,
            categorySelections,
            categorySelectAll,
            draftShowOnly,
            defaultShowOnly,
            selectedCategory,
            selectedCategoryOptions,
            fetchedCategoryTotalCount,
            debouncedCategorySearch,
            onApply,
            setCategorySelections,
            setCategorySelectAll,
            setDraftShowOnly,
            setSelectedCategoryKey,
            setIsEditedActionsOpen,
        });

    useEffect(() => {
        if (!isEditedActionsOpen) return;
        if (resolvedEditedActionTypes.length === 0) return;
        setDraftShowOnly((current) => {
            if (!current.edited || current.editedFilters.length > 0) return current;
            return {
                ...current,
                editedFilters: resolvedEditedActionTypes.map(({ id, input_value }) => ({ id, input_value })),
            };
        });
    }, [isEditedActionsOpen, resolvedEditedActionTypes]);

    const handleEditedToggle = (checked: boolean) => {
        if (!checked) {
            setDraftShowOnly({
                edited: false,
                impacted: false,
                editedFilters: [],
            });
            setIsEditedActionsOpen(false);
            return;
        }

        const availableActionTypes =
            isEditedActionsOpen && resolvedEditedActionTypes.length > 0
                ? resolvedEditedActionTypes
                : editedActionTypes;

        setDraftShowOnly((current) => ({
            edited: true,
            impacted: current.impacted,
            editedFilters:
                current.editedFilters.length > 0
                    ? current.editedFilters
                    : availableActionTypes.map(({ id, input_value }) => ({ id, input_value })),
        }));
    };

    const handleImpactedToggle = (checked: boolean) => {
        setDraftShowOnly((current) => ({
            edited: checked ? true : current.edited,
            impacted: checked,
            editedFilters: current.editedFilters,
        }));
    };

    const handleEditedActionSelectionChange = (selectedInputValues: string[]) => {
        setDraftShowOnly((current) => ({
            ...current,
            edited: true,
            editedFilters: toSimulatorShowOnlyFilterItems(resolvedEditedActionTypes, selectedInputValues),
        }));
    };

    const handleEditedRowClick = () => {
        if (!canExpandEditedActions) return;
        setSelectedCategoryKey(null);
        const isOpening = !isEditedActionsOpen;
        setIsEditedActionsOpen((current) => !current);

        if (isOpening && draftShowOnly.edited && draftShowOnly.editedFilters.length === 0 && editedActionTypes.length > 0) {
            setDraftShowOnly((current) => ({
                ...current,
                editedFilters: editedActionTypes.map(({ id, input_value }) => ({ id, input_value })),
            }));
        }
    };

    const handleCategoryClick = (categoryKey: string) => {
        setIsEditedActionsOpen(false);
        setSelectedCategoryKey((currentKey) => (currentKey === categoryKey ? null : categoryKey));
    };

    const isCategoryActive = (categoryKey: string) => {
        const category = filterCategories.find((item) => item.key === categoryKey);
        const hasSelections =
            (categorySelections[categoryKey]?.length ?? 0) > 0 || Boolean(categorySelectAll[categoryKey]);

        return hasSelections || Boolean(category?.isDefaultSelected);
    };

    const isEditedActionsActive =
        isEditedActionsOpen || draftShowOnly.editedFilters.length > 0 || draftShowOnly.edited;

    return (
        <StyledFilterPanelsRoot>
            {isEditedActionsOpen ? (
                <SimulatorFilterCategoryPanel
                    key="edited-actions"
                    categoryKey="edited_actions"
                    categoryLabel="Action types"
                    options={editedActionOptions}
                    isLoading={isEditedActionsLoading}
                    selectedOptionIds={selectedEditedActionValues}
                    onSelectedOptionIdsChange={handleEditedActionSelectionChange}
                />
            ) : null}

            {selectedCategory ? (
                <SimulatorFilterCategoryPanel
                    key={selectedCategory.key}
                    categoryKey={selectedCategory.key}
                    categoryLabel={selectedCategory.label}
                    options={selectedCategoryOptions}
                    isLoading={isCategoryOptionsLoading}
                    isFetchingMore={isCategoryOptionsFetchingMore}
                    isCategorySelectAll={Boolean(categorySelectAll[selectedCategory.key])}
                    totalOptionCount={fetchedCategoryTotalCount}
                    selectedOptionIds={categorySelections[selectedCategory.key] ?? []}
                    onSelectedOptionIdsChange={(ids) => {
                        handleCategorySelectionChange(
                            selectedCategory.key,
                            ids,
                            Boolean(categorySelectAll[selectedCategory.key]),
                        );
                    }}
                    onOptionToggle={(optionId, checked) => {
                        handleCategoryOptionToggle(
                            selectedCategory.key,
                            optionId,
                            checked,
                            Boolean(categorySelectAll[selectedCategory.key]),
                        );
                    }}
                    onSelectAllChange={handleCategorySelectAllChange}
                    useServerSideSearch
                    onSearchKeywordChange={setCategorySearchInput}
                    onOptionsScroll={handleCategoryOptionsScroll}
                />
            ) : null}

            <StyledPanel data-testid={PPA_SIM_FILTER_PANEL}>
                <div className="filter-panel-section filter-panel-header">
                    <div className="filter-panel-title-wrap">
                        <p className="filter-panel-title">Filter</p>
                    </div>
                    <div className="filter-panel-search">
                        <PpaSplitSearchInput
                            width={PPA_SPLIT_SEARCH_WIDTH.FILTER_PANEL}
                            value={searchQuery}
                            onChange={setSearchQuery}
                            ariaLabel="Search filters"
                            submitAriaLabel="Submit filter search"
                            inputTestId={PPA_SIM_FILTER_SEARCH_INPUT}
                            submitTestId={PPA_SIM_FILTER_SEARCH_SUBMIT}
                        />
                    </div>
                </div>

                <div className="filter-panel-divider-wrap">
                    <Divider className="filter-panel-divider" />
                </div>

                <div className="filter-panel-show-only-scroll">
                    <div className="filter-panel-section filter-show-only-section">
                        <p className="filter-show-only-label">Show only</p>

                        <div
                            className={`filter-menu-item filter-show-only-item${
                                isEditedActionsActive ? ' active' : ''
                            }`}
                            data-testid={PPA_SIM_FILTER_SHOW_EDITED_BTN}
                            onClick={canExpandEditedActions ? handleEditedRowClick : undefined}
                        >
                            <div
                                className="filter-menu-item-left"
                                onClick={(event) => event.stopPropagation()}
                            >
                                <Switch
                                    size="small"
                                    checked={draftShowOnly.edited}
                                    onChange={handleEditedToggle}
                                    data-testid={PPA_SIM_FILTER_SHOW_EDITED_TOGGLE}
                                />
                                <span className="filter-show-only-item-label">
                                    {showOnlyLabels.edited}
                                </span>
                            </div>
                            {canExpandEditedActions ? (
                                <span className="filter-menu-chevron" aria-hidden>
                                    <ChevronRight />
                                </span>
                            ) : null}
                        </div>

                        <div
                            className="filter-menu-item filter-show-only-item"
                            data-testid={PPA_SIM_FILTER_SHOW_IMPACTED_BTN}
                        >
                            <div className="filter-menu-item-left">
                                <Switch
                                    size="small"
                                    checked={draftShowOnly.impacted}
                                    onChange={handleImpactedToggle}
                                    data-testid={PPA_SIM_FILTER_SHOW_IMPACTED_TOGGLE}
                                />
                                <span className="filter-show-only-item-label">
                                    {showOnlyLabels.impacted}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="filter-panel-divider-wrap">
                    <Divider className="filter-panel-inner-divider" />
                </div>

                <div className="filter-panel-categories-scroll">
                    <div className="filter-panel-section filter-categories-section">
                        {hasNoFilterSearchResults ? (
                            <SearchEmptyResult />
                        ) : (
                            visibleFilterCategories.map((category) => (
                                <div
                                    key={category.key}
                                    className={`filter-menu-item${
                                        isCategoryActive(category.key) ? ' active' : ''
                                    }`}
                                    data-testid={ppaSimFilterCategoryBtnTestId(category.key)}
                                    onClick={() => handleCategoryClick(category.key)}
                                >
                                    <span
                                        className={`filter-category-label${
                                            isCategoryActive(category.key)
                                                ? ' filter-category-label-active'
                                                : ''
                                        }`}
                                    >
                                        {category.label}
                                    </span>
                                    <span className="filter-menu-chevron" aria-hidden>
                                        <ChevronRight />
                                    </span>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                <div className="filter-panel-divider-wrap">
                    <Divider className="filter-panel-divider" />
                </div>

                <SimulatorFilterPanelFooter
                    footerActions={footerActions}
                    isApplyLoading={isApplyLoading}
                    isApplyingFilters={isApplyingFilters}
                    onFooterAction={handleFooterAction}
                />
            </StyledPanel>
        </StyledFilterPanelsRoot>
    );
};

export default SimulatorFilterPanel;
